import { useMemo } from 'react';
import {
  Target,
  Waypoints,
  ListChecks,
  FileText,
  Scale,
  Server,
  Newspaper,
  CalendarDays,
  Ban,
  UserRound,
  ArrowRight,
  ExternalLink,
  Crosshair,
  type LucideIcon,
} from 'lucide-react';
import { Badge, Button, Card, CardBody, cn, type BadgeTone } from '../../ui';
import {
  neighborsOf,
  nodeKindLabel,
  type ConnectedSubgraph,
  type EntityGraph,
  type GraphEdgeLayer,
  type GraphNode,
  type GraphNodeKind,
  type NeighborEdge,
} from '../../features/graph/graphModel';

const KIND_ICON: Record<GraphNodeKind, LucideIcon> = {
  goal: Target,
  workstream: Waypoints,
  story: ListChecks,
  document: FileText,
  decision: Scale,
  service: Server,
  change: Newspaper,
  dailyUpdate: CalendarDays,
  blocker: Ban,
  person: UserRound,
};

const KIND_TONE: Record<GraphNodeKind, BadgeTone> = {
  goal: 'accent',
  workstream: 'info',
  story: 'neutral',
  document: 'info',
  decision: 'warning',
  service: 'neutral',
  change: 'neutral',
  dailyUpdate: 'neutral',
  blocker: 'danger',
  person: 'success',
};

const LAYER_TONE: Record<GraphEdgeLayer, BadgeTone> = {
  goal: 'accent',
  knowledge: 'info',
  execution: 'warning',
  people: 'success',
};

const LAYER_LABEL: Record<GraphEdgeLayer, string> = {
  goal: 'Goals',
  knowledge: 'Knowledge',
  execution: 'Execution',
  people: 'People',
};

const LAYER_ORDER: GraphEdgeLayer[] = ['goal', 'execution', 'knowledge', 'people'];

export interface GraphViewProps {
  graph: EntityGraph;
  subgraph: ConnectedSubgraph;
  /** Re-root the explorer on a node (click-through within the graph). */
  onSelectNode: (key: string) => void;
  /** Navigate to a node's entity route (open the underlying entity). */
  onOpenNode: (node: GraphNode) => void;
}

/** A compact, clickable node chip (kind icon + title). */
function NodeChip({
  node,
  onSelect,
  onOpen,
  emphasis,
}: {
  node: GraphNode;
  onSelect: () => void;
  onOpen: () => void;
  emphasis?: boolean;
}) {
  const Icon = KIND_ICON[node.kind];
  const tone = KIND_TONE[node.kind];
  const canOpen = !!node.route;
  return (
    <span className="inline-flex items-center">
      <button
        type="button"
        onClick={onSelect}
        title={`Explore ${nodeKindLabel(node.kind)}: ${node.title}`}
        className={cn(
          'inline-flex max-w-full items-center rounded-l-full',
          !canOpen && 'rounded-r-full',
        )}
      >
        <Badge
          tone={tone}
          className={cn(
            'rounded-r-none',
            emphasis && 'ring-1 ring-inset ring-accent/50',
          )}
        >
          <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="max-w-[15rem] truncate">{node.title}</span>
        </Badge>
      </button>
      {canOpen && (
        <button
          type="button"
          onClick={onOpen}
          title={`Open ${nodeKindLabel(node.kind)}`}
          className="inline-flex items-center rounded-r-full border border-l-0 border-surface-border bg-surface-3 px-1.5 py-0.5 text-zinc-400 transition-colors hover:text-zinc-100"
        >
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

/**
 * Renders a selected root's connected graph as a clean, readable relationship
 * view: a focused root card, its DIRECT connections grouped by layer (each row
 * = relationship label + neighbour chip), and an EXTENDED section listing nodes
 * reached at greater depth. Clicking any node re-roots the explorer; the ↗
 * affordance opens the underlying entity. A dark, node-link-style layout with
 * click-through — no metrics, purely connections + navigation.
 */
export function GraphView({ graph, subgraph, onSelectNode, onOpenNode }: GraphViewProps) {
  const { root } = subgraph;
  const RootIcon = KIND_ICON[root.kind];

  // Direct neighbours of the root, grouped by edge layer.
  const directByLayer = useMemo(() => {
    const neighbors = neighborsOf(graph, root.key);
    const byLayer = new Map<GraphEdgeLayer, NeighborEdge[]>();
    for (const ne of neighbors) {
      const list = byLayer.get(ne.edge.layer) ?? [];
      list.push(ne);
      byLayer.set(ne.edge.layer, list);
    }
    for (const list of byLayer.values()) {
      list.sort((a, b) => a.neighbor.title.localeCompare(b.neighbor.title));
    }
    return byLayer;
  }, [graph, root.key]);

  // Nodes reached beyond depth 1, grouped by kind.
  const extendedByKind = useMemo(() => {
    const byKind = new Map<GraphNodeKind, GraphNode[]>();
    for (const { node, depth } of subgraph.nodes) {
      if (depth < 2) continue;
      const list = byKind.get(node.kind) ?? [];
      list.push(node);
      byKind.set(node.kind, list);
    }
    for (const list of byKind.values()) {
      list.sort((a, b) => a.title.localeCompare(b.title));
    }
    return byKind;
  }, [subgraph.nodes]);

  const directCount = subgraph.nodes.filter((n) => n.depth === 1).length;
  const extendedCount = subgraph.nodes.filter((n) => n.depth >= 2).length;

  return (
    <div className="space-y-5">
      {/* Root focus card */}
      <Card>
        <CardBody className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-3 text-accent">
              <RootIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge tone={KIND_TONE[root.kind]}>{nodeKindLabel(root.kind)}</Badge>
                {root.subtitle && (
                  <span className="text-xs text-zinc-500">{root.subtitle}</span>
                )}
              </div>
              <h2 className="mt-1 text-base font-semibold text-zinc-100">
                {root.title}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-4 text-right">
            <div className="text-xs text-zinc-500">
              <span className="block text-sm font-semibold text-zinc-200">
                {subgraph.nodes.length}
              </span>
              connected
            </div>
            <div className="text-xs text-zinc-500">
              <span className="block text-sm font-semibold text-zinc-200">
                {subgraph.edges.length}
              </span>
              relationships
            </div>
            {root.route && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<ExternalLink className="h-4 w-4" />}
                onClick={() => onOpenNode(root)}
              >
                Open
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Direct connections, grouped by layer */}
      {directCount === 0 ? (
        <Card>
          <CardBody className="text-sm text-zinc-500">
            This node has no recorded connections yet. Link it to goals, work,
            documents or decisions to build out the graph.
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            <Crosshair className="h-3.5 w-3.5" aria-hidden="true" />
            Direct connections ({directCount})
          </div>
          {LAYER_ORDER.filter((l) => directByLayer.has(l)).map((layer) => {
            const items = directByLayer.get(layer) ?? [];
            return (
              <Card key={layer}>
                <CardBody className="space-y-2.5">
                  <div className="flex items-center gap-2">
                    <Badge tone={LAYER_TONE[layer]} dot>
                      {LAYER_LABEL[layer]}
                    </Badge>
                    <span className="text-xs text-zinc-500">{items.length}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {items.map(({ neighbor, edge, outward }) => (
                      <li
                        key={`${edge.from}|${edge.to}|${edge.label}`}
                        className="flex flex-wrap items-center gap-2 text-sm"
                      >
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                          {outward ? '' : '←'} {edge.label}{' '}
                          <ArrowRight className="h-3 w-3" aria-hidden="true" />
                        </span>
                        <NodeChip
                          node={neighbor}
                          onSelect={() => onSelectNode(neighbor.key)}
                          onOpen={() => onOpenNode(neighbor)}
                        />
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* Extended graph (depth >= 2) */}
      {extendedCount > 0 && (
        <div className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            Extended graph ({extendedCount})
          </div>
          <Card>
            <CardBody className="space-y-3">
              {[...extendedByKind.entries()].map(([kind, list]) => (
                <div key={kind}>
                  <p className="mb-1.5 text-[11px] text-zinc-500">
                    {nodeKindLabel(kind)}s ({list.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((node) => (
                      <NodeChip
                        key={node.key}
                        node={node}
                        onSelect={() => onSelectNode(node.key)}
                        onOpen={() => onOpenNode(node)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

export default GraphView;
